/**
 * Envoi d'une pièce au client depuis le téléphone.
 *
 * L'application n'expédie rien elle-même : elle ouvre la messagerie, WhatsApp
 * ou les SMS de l'appareil, pré-remplis. L'artisan relit et envoie. C'est plus
 * sûr qu'un envoi automatique — il voit ce qui part — et cela évite d'avoir à
 * configurer un serveur d'envoi sur chaque téléphone.
 */

import { dateEcheance, joursRetard } from './dates';
import { tr, trf } from './i18n';

/** Indicatif suisse, pour les numéros écrits à la locale. */
const INDICATIF_SUISSE = '41';

/**
 * Met un numéro à la forme internationale attendue par WhatsApp.
 *
 * WhatsApp exige l'indicatif pays, sans `+` ni séparateur. Un numéro noté à la
 * suisse — `021 000 00 00` — n'a pas d'indicatif et son zéro initial est un
 * préfixe national qui disparaît à l'international : l'envoyer tel quel ouvre
 * une conversation avec un numéro qui n'existe pas.
 *
 * @param {string} tel numéro tel que saisi
 * @returns {string} le numéro international, ou une chaîne vide
 */
export function numeroInternational(tel) {
  const brut = String(tel || '').replace(/[^\d+]/g, '');
  if (!brut) {
    return '';
  }
  if (brut.startsWith('+')) {
    return brut.slice(1);
  }
  if (brut.startsWith('00')) {
    return brut.slice(2);
  }
  // Un numéro commençant par 0 est national : le 0 cède la place à l'indicatif.
  if (brut.startsWith('0')) {
    return INDICATIF_SUISSE + brut.slice(1);
  }
  return brut;
}

/**
 * Objet et corps du message accompagnant une pièce.
 *
 * <p>Par WhatsApp ou SMS, le message partait seul : « Veuillez trouver notre
 * Offre 03 », sans l'offre. Le lien personnel du client y prend place — il
 * ouvre la pièce, la télécharge en PDF et, pour une offre, l'accepte.
 *
 * <p>Le message salue le client par son nom et dit quelle entreprise envoie la
 * pièce, puis signe de son nom : un lien reçu sans savoir de qui il vient ni à
 * qui il s'adresse passe pour une arnaque, et le client n'ose pas l'ouvrir.
 *
 * @param {object} piece devis ou facture
 * @param {string} nature « Devis », « Offre », « Facture »
 * @param {object} soc coordonnées de l'entreprise
 * @param {string} [lien] lien personnel du client vers la pièce
 * @param {string} [client] nom du client à qui la pièce est adressée
 * @param {{montant: string}} [relance] une relance : la facture échue ou
 *   l'offre restée sans réponse, avec son montant. Elle part comme l'envoi de
 *   la pièce — salutation, lien, PDF joint, signature — et non plus en une
 *   ligne de texte (« Rappel : la facture 22… »).
 * @returns {{titre: string, message: string}}
 */
export function composeMessage(piece, nature, soc, lien, client, relance) {
  const titre = (nature + ' ' + (piece?.num || piece?.id || '')).trim();
  const pronom = /^(devis|avoir)$/i.test(nature) ? 'le' : 'la';
  // Le message part dans la langue de l'entreprise, comme ses documents.
  const usage = /^avoir$/i.test(nature)
    ? tr('Vous pouvez le consulter et le télécharger en PDF.')
    : /^facture$/i.test(nature)
      ? trf(
          'Vous pouvez {0} consulter et {1} télécharger en PDF, avec le QR-facture pour le paiement.',
          pronom,
          pronom,
        )
      : trf(
          'Vous pouvez {0} consulter, {1} télécharger en PDF et l’accepter en ligne.',
          pronom,
          pronom,
        );
  const societe = (soc?.nom || '').trim();
  const nomClient = (client || '').trim();
  const salut = nomClient ? trf('Bonjour {0},', nomClient) : tr('Bonjour,');
  // Une facture échue part avec l'avertissement du prochain rappel : le
  // patron renvoyait la facture par WhatsApp sans que le client sache que
  // des frais s'ajouteraient.
  const echue =
    /^facture$/i.test(nature) &&
    ['Envoyée', 'Partielle'].includes(piece?.statut) &&
    joursRetard(piece) > 0;
  const avertissement = echue
    ? '\n\n' +
      trf(
        '⚠️ Cette facture est échue depuis le {0}. Sans paiement de votre part, des frais supplémentaires seront ajoutés au prochain rappel.',
        dateEcheance(piece),
      )
    : '';
  if (relance) {
    const facture = /^facture$/i.test(nature);
    const objet = facture
      ? joursRetard(piece) > 0
        ? trf(
            '{0} vous rappelle : {1} de {2}, échue depuis le {3}.',
            societe,
            titre,
            relance.montant,
            dateEcheance(piece),
          )
        : trf('{0} vous rappelle : {1} de {2}.', societe, titre, relance.montant)
      : trf('{0} revient vers vous au sujet de : {1} de {2}.', societe, titre, relance.montant);
    const suite = facture
      ? tr('Merci de procéder au paiement.') + ' ' + usage
      : usage + ' ' + tr('N’hésitez pas à nous contacter pour toute question.');
    // Ligne à ligne : chaque morceau se traduit seul.
    return {
      titre,
      message: [salut, '', objet, lien || null, '', suite, '', tr('Cordialement,'), societe]
        .filter((l) => l !== null)
        .join('\n'),
    };
  }
  if (lien) {
    // « Le client voit le nom de la société plus son nom à lui : il sait que ce
    // n'est pas une arnaque. » De qui, pour qui, quoi ; puis le lien, dont
    // WhatsApp, Messages ou Telegram dessinent l'aperçu de la feuille A4.
    // Ligne à ligne : chaque morceau se traduit seul.
    return {
      titre,
      message: [
        societe ? trf('🏢 De : {0}', societe) : null,
        nomClient ? trf('👤 Pour : {0}', nomClient) : null,
        '📄 ' + titre,
        '',
        salut,
        usage,
        lien,
        avertissement ? avertissement.trim() : null,
        '',
        tr('Cordialement,'),
        societe,
      ]
        .filter((l) => l !== null)
        .join('\n'),
    };
  }
  const message = lien
    ? ''
    : trf(
        '{0}\n\n{1} vous envoie : {2}.\n\nCordialement,\n{1}',
        salut + avertissement,
        societe,
        titre,
      );
  return { titre, message };
}

/**
 * Liens d'envoi pour chaque canal.
 *
 * Un canal sans destinataire est tout de même proposé : ouvrir la messagerie
 * vide permet de choisir le contact à la main, ce qui dépanne quand la fiche
 * client est incomplète.
 *
 * @param {object} client destinataire
 * @param {{titre: string, message: string}} contenu message composé
 * @returns {Array<{cle: string, icone: string, nom: string, cible: string, href: string, externe: boolean}>}
 */
export function liensEnvoi(client, { titre, message }) {
  const email = client?.email || '';
  const telBrut = client?.tel || client?.telephone || '';
  const tel = numeroInternational(telBrut);
  const msg = encodeURIComponent(message);

  return [
    {
      cle: 'email',
      icone: '📧',
      nom: 'E-mail',
      cible: email || 'ouvrir la messagerie',
      href: 'mailto:' + email + '?subject=' + encodeURIComponent(titre) + '&body=' + msg,
      externe: false,
    },
    {
      cle: 'whatsapp',
      icone: '💬',
      nom: 'WhatsApp',
      cible: telBrut || 'numéro du client',
      href: 'https://wa.me/' + tel + '?text=' + msg,
      externe: true,
    },
    {
      cle: 'sms',
      icone: '✉️',
      nom: 'SMS',
      cible: telBrut || 'numéro du client',
      // Le point-virgule sépare le destinataire du corps sur iOS comme sur
      // Android ; le `?` de la forme usuelle n'est compris que par l'un.
      href: 'sms:' + telBrut.replace(/\s/g, '') + ';body=' + msg,
      externe: false,
    },
  ];
}

/**
 * L'aperçu large d'un lien de pièce, celui que lisent WhatsApp et Messages :
 * `…/p/<jeton>` devient `…/api/public/pages/<jeton>/apercu.jpg?format=lien`.
 *
 * @param {string} [url] lien de la pièce
 * @returns {string|null} l'adresse de l'aperçu, nulle sans lien
 */
export function adresseApercuLien(url) {
  const m = /^(.*)\/p\/([A-Za-z0-9_-]+)/.exec(url || '');
  return m ? `${m[1]}/api/public/pages/${m[2]}/apercu.jpg?format=lien` : null;
}
